import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  AGENDA_SLOT_HEIGHT_PX,
  AGENDA_SLOT_MINUTES,
  buildHourSlotLabels,
  computeDropPreview,
  computeGridPosition,
  computeNowIndicatorPercent,
  countGridSlots,
  getGridBoundsMinutes,
  minutesFromPointerY,
  parseDatetimeToMinutes,
  type DropPreviewState,
} from "../../../../helpers/agendaDayGrid";
import type { AgendaGridBlock, AgendaGridColumn, AgendaGridTimeBounds } from "../../../../helpers/agendaGridTypes";
import { Appointment } from "../../../../services/appointmentService";
import AgendaGridEventCard from "./AgendaGridEventCard";

const DRAG_MIME = "application/x-adatis-appointment-id";
const COMPACT_COLUMN_THRESHOLD = 4;

type AgendaCustomGridProps = {
  mode: "day" | "week";
  selectedDate: string;
  columns: AgendaGridColumn[];
  blocks: AgendaGridBlock[];
  bounds: AgendaGridTimeBounds;
  scrollTime: string;
  showNowIndicator: boolean;
  selectedAppointmentId: number | null;
  resolveStatusLabel: (status: string) => string;
  showProfessionalInTooltip?: boolean;
  onSlotClick: (params: {
    date: string;
    columnId: string;
    minutesFromMidnight: number;
  }) => void;
  onEventClick: (appointment: Appointment) => void;
  onFillHealthRecord?: (appointment: Appointment) => void;
  onViewEvolution?: (appointment: Appointment) => void;
  onClearArrived?: (appointment: Appointment) => void;
  onEventDrop?: (params: {
    appointment: Appointment;
    newColumnId: string;
    newStartDatetime: string;
  }) => Promise<void>;
  onColumnHeaderClick?: (columnId: string) => void;
  onAbsenceClick?: (columnId: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
};

export type AgendaCustomGridHandle = {
  scrollToTime: (time: string) => void;
};

type DragState = {
  appointmentId: number;
  durationMinutes: number;
};

const AgendaCustomGrid = forwardRef<AgendaCustomGridHandle, AgendaCustomGridProps>(({
  mode,
  selectedDate,
  columns,
  blocks,
  bounds,
  scrollTime,
  showNowIndicator,
  selectedAppointmentId,
  resolveStatusLabel,
  showProfessionalInTooltip = false,
  onSlotClick,
  onEventClick,
  onFillHealthRecord,
  onViewEvolution,
  onClearArrived,
  onEventDrop,
  onColumnHeaderClick,
  onAbsenceClick,
  scrollContainerRef,
}, ref) => {
  const { t } = useTranslation();
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = scrollContainerRef ?? internalScrollRef;
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const columnsTrackRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);

  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dropPreview, setDropPreview] = useState<DropPreviewState | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const isCompactMode = mode === "day" && columns.length > COMPACT_COLUMN_THRESHOLD;
  const slotCount = useMemo(() => countGridSlots(bounds), [bounds]);
  const gridHeightPx = slotCount * AGENDA_SLOT_HEIGHT_PX;
  const hourLabels = useMemo(() => buildHourSlotLabels(bounds), [bounds]);
  const gridBoundsMinutes = useMemo(() => getGridBoundsMinutes(bounds), [bounds]);

  const blocksByColumn = useMemo(() => {
    const map = new Map<string, AgendaGridBlock[]>();

    columns.forEach((column) => {
      map.set(column.id, []);
    });

    blocks.forEach((block) => {
      const columnId =
        mode === "week" ? block.date : String(block.professionalId);

      if (mode === "day" && columns.length === 1 && columns[0]?.id === "all") {
        const bucket = map.get("all");

        if (bucket) {
          bucket.push(block);
        }

        return;
      }

      const bucket = map.get(columnId);

      if (bucket) {
        bucket.push(block);
      }
    });

    return map;
  }, [blocks, columns, mode]);

  const nowIndicatorPercent = useMemo(() => {
    if (!showNowIndicator) {
      return null;
    }

    return computeNowIndicatorPercent(bounds, new Date(nowTick));
  }, [bounds, nowTick, showNowIndicator]);

  useEffect(() => {
    if (!showNowIndicator) {
      return undefined;
    }

    const interval = window.setInterval(() => setNowTick(Date.now()), 60_000);

    return () => window.clearInterval(interval);
  }, [showNowIndicator]);

  const syncHorizontalScroll = useCallback((source: "header" | "body", scrollLeft: number) => {
    if (isSyncingScrollRef.current) {
      return;
    }

    isSyncingScrollRef.current = true;

    if (source === "body" && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = scrollLeft;
    }

    if (source === "header" && columnsTrackRef.current) {
      columnsTrackRef.current.scrollLeft = scrollLeft;
    }

    window.requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  }, []);

  const scrollToMinutes = useCallback(
    (time: string) => {
      const element = bodyRef.current;

      if (!element) {
        return;
      }

      const { start, total } = getGridBoundsMinutes(bounds);
      const targetMinutes = parseDatetimeToMinutes(`2000-01-01 ${time}`);
      const ratio = (targetMinutes - start) / total;
      const targetScroll = ratio * element.scrollHeight - element.clientHeight * 0.25;

      element.scrollTop = Math.max(0, targetScroll);
    },
    [bodyRef, bounds]
  );

  useImperativeHandle(ref, () => ({ scrollToTime: scrollToMinutes }), [scrollToMinutes]);

  useEffect(() => {
    scrollToMinutes(scrollTime);
  }, [scrollTime, bounds.slotMinTime, bounds.slotMaxTime, scrollToMinutes]);

  const handleColumnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, column: AgendaGridColumn) => {
      if ((event.target as HTMLElement).closest(".agenda-grid-event")) {
        return;
      }

      const columnRect = event.currentTarget.getBoundingClientRect();
      const minutes = minutesFromPointerY(event.clientY, columnRect, bounds);
      const date = mode === "week" ? column.id : selectedDate;

      onSlotClick({
        date,
        columnId: column.id,
        minutesFromMidnight: minutes,
      });
    },
    [bounds, mode, onSlotClick, selectedDate]
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLButtonElement>, block: AgendaGridBlock) => {
      if (!block.draggable) {
        event.preventDefault();
        return;
      }

      const startMinutes = parseDatetimeToMinutes(block.startDatetime);
      const endMinutes = parseDatetimeToMinutes(block.endDatetime);

      event.dataTransfer.setData(DRAG_MIME, String(block.appointment.id));
      event.dataTransfer.effectAllowed = "move";
      setDragState({
        appointmentId: block.appointment.id,
        durationMinutes: Math.max(endMinutes - startMinutes, AGENDA_SLOT_MINUTES),
      });
    },
    []
  );

  const handleDragEnd = useCallback(() => {
    setDragState(null);
    setDropPreview(null);
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, column: AgendaGridColumn) => {
      if (!event.dataTransfer.types.includes(DRAG_MIME) || !dragState) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      const columnBlocks = blocksByColumn.get(column.id) ?? [];
      const preview = computeDropPreview(
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
        column.id,
        dragState.durationMinutes,
        dragState.appointmentId,
        columnBlocks,
        bounds
      );

      setDropPreview(preview);
    },
    [blocksByColumn, bounds, dragState]
  );

  const handleDragLeave = useCallback((columnId: string) => {
    setDropPreview((current) => (current?.columnId === columnId ? null : current));
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>, column: AgendaGridColumn) => {
      event.preventDefault();

      const rawId = event.dataTransfer.getData(DRAG_MIME);
      const appointmentId = Number(rawId);

      if (!Number.isFinite(appointmentId) || !dragState || !onEventDrop) {
        setDragState(null);
        setDropPreview(null);
        return;
      }

      const block = blocks.find(
        (item) => item.kind === "appointment" && item.appointment.id === appointmentId
      );

      if (!block) {
        setDragState(null);
        setDropPreview(null);
        return;
      }

      const columnBlocks = blocksByColumn.get(column.id) ?? [];
      const preview = computeDropPreview(
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
        column.id,
        dragState.durationMinutes,
        dragState.appointmentId,
        columnBlocks,
        bounds
      );

      setDropPreview(null);
      setDragState(null);

      if (preview.hasConflict) {
        toast.info(t("studio.agenda.calendar.dropConflict"));
        return;
      }

      const columnRect = event.currentTarget.getBoundingClientRect();
      let minutes = minutesFromPointerY(event.clientY, columnRect, bounds);
      const { end } = getGridBoundsMinutes(bounds);

      minutes = Math.min(minutes, end - dragState.durationMinutes);

      const date = mode === "week" ? column.id : selectedDate;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const newStartDatetime = `${date} ${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;

      await onEventDrop({
        appointment: block.appointment,
        newColumnId: column.id,
        newStartDatetime,
      });
    },
    [blocks, blocksByColumn, bounds, dragState, mode, onEventDrop, selectedDate, t]
  );

  if (columns.length === 0) {
    return (
      <div className="agenda-custom-grid agenda-custom-grid--empty text-center text-muted py-5">
        {t("studio.agenda.calendar.gridEmpty")}
      </div>
    );
  }

  return (
    <div
      className={classNames("agenda-custom-grid", {
        "agenda-custom-grid--week": mode === "week",
        "agenda-custom-grid--day": mode === "day",
        "agenda-custom-grid--compact": isCompactMode,
      })}
    >
      <div className="agenda-custom-grid__header">
        <div className="agenda-custom-grid__corner" aria-hidden="true" />
        <div
          className="agenda-custom-grid__columns-header"
          ref={headerScrollRef}
          onScroll={(event) => syncHorizontalScroll("header", event.currentTarget.scrollLeft)}
        >
          {columns.map((column) => (
            <div
              key={column.id}
              className={classNames("agenda-custom-grid__column-header", {
                "agenda-custom-grid__column-header--today": column.isToday,
                "agenda-custom-grid__column-header--closed": column.isClosed,
                "agenda-custom-grid__column-header--clickable": Boolean(onColumnHeaderClick),
              })}
              onClick={onColumnHeaderClick ? () => onColumnHeaderClick(column.id) : undefined}
              onKeyDown={
                onColumnHeaderClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onColumnHeaderClick(column.id);
                      }
                    }
                  : undefined
              }
              role={onColumnHeaderClick ? "button" : undefined}
              tabIndex={onColumnHeaderClick ? 0 : undefined}
            >
              {column.accentColor ? (
                <span
                  className="agenda-custom-grid__column-dot"
                  style={{ backgroundColor: column.accentColor }}
                  aria-hidden="true"
                />
              ) : null}
              <span className="agenda-custom-grid__column-title">{column.title}</span>
              {column.subtitle ? (
                <span className="agenda-custom-grid__column-subtitle">{column.subtitle}</span>
              ) : null}
              {onAbsenceClick && mode === "day" && column.id !== "all" ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 agenda-custom-grid__absence"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAbsenceClick(column.id);
                  }}
                >
                  {t("studio.agenda.absence.shortcut")}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="agenda-custom-grid__scroll" ref={bodyRef}>
        <div className="agenda-custom-grid__body" style={{ minHeight: `${gridHeightPx}px` }}>
          <div className="agenda-custom-grid__time-gutter" aria-hidden="true">
            {hourLabels.map((slot) => (
              <div
                key={slot.minutes}
                className="agenda-custom-grid__time-label"
                style={{
                  top: `${((slot.minutes - gridBoundsMinutes.start) / gridBoundsMinutes.total) * 100}%`,
                }}
              >
                {slot.label}
              </div>
            ))}
          </div>

          <div
            className="agenda-custom-grid__columns-track"
            ref={columnsTrackRef}
            onScroll={(event) => syncHorizontalScroll("body", event.currentTarget.scrollLeft)}
          >
            <div className="agenda-custom-grid__columns" style={{ minHeight: `${gridHeightPx}px` }}>
              {columns.map((column) => {
                const columnBlocks = blocksByColumn.get(column.id) ?? [];
                const isDropTarget = dropPreview?.columnId === column.id;

                return (
                  <div
                    key={column.id}
                    className={classNames("agenda-custom-grid__column", {
                      "agenda-custom-grid__column--closed": column.isClosed,
                      "agenda-custom-grid__column--drop-target": isDropTarget && !dropPreview?.hasConflict,
                      "agenda-custom-grid__column--drop-conflict": isDropTarget && dropPreview?.hasConflict,
                    })}
                    style={{ minHeight: `${gridHeightPx}px` }}
                    onClick={(event) => handleColumnClick(event, column)}
                    onDragOver={(event) => handleDragOver(event, column)}
                    onDragLeave={() => handleDragLeave(column.id)}
                    onDrop={(event) => void handleDrop(event, column)}
                  >
                    <div className="agenda-custom-grid__slot-grid" aria-hidden="true">
                      {Array.from({ length: slotCount }, (_, index) => (
                        <div key={index} className="agenda-custom-grid__slot-line" />
                      ))}
                    </div>

                    {isDropTarget && dropPreview ? (
                      <div
                        className={classNames("agenda-custom-grid__drop-preview", {
                          "agenda-custom-grid__drop-preview--conflict": dropPreview.hasConflict,
                        })}
                        style={{
                          top: `${dropPreview.topPercent}%`,
                          height: `${dropPreview.heightPercent}%`,
                        }}
                        aria-hidden="true"
                      />
                    ) : null}

                    {showNowIndicator && nowIndicatorPercent !== null && (mode === "day" || column.isToday) ? (
                      <div
                        className="agenda-custom-grid__now-line"
                        style={{ top: `${nowIndicatorPercent}%` }}
                        aria-hidden="true"
                      >
                        <span className="agenda-custom-grid__now-dot" />
                      </div>
                    ) : null}

                    {columnBlocks.map((block) => {
                      const position = computeGridPosition(
                        block.startDatetime,
                        block.endDatetime,
                        bounds
                      );

                      if (!position) {
                        return null;
                      }

                      if (block.kind === "buffer") {
                        return (
                          <div
                            key={block.id}
                            className={classNames("agenda-grid-event agenda-grid-event--buffer", block.classNames)}
                            style={{
                              top: `${position.topPercent}%`,
                              height: `${position.heightPercent}%`,
                            }}
                            title={block.title}
                          >
                            <span className="agenda-grid-event__buffer-label">{block.title}</span>
                          </div>
                        );
                      }

                      return (
                        <AgendaGridEventCard
                          key={block.id}
                          block={block}
                          position={position}
                          isSelected={selectedAppointmentId === block.appointment.id}
                          resolveStatusLabel={resolveStatusLabel}
                          showProfessional={showProfessionalInTooltip}
                          onClick={() => onEventClick(block.appointment)}
                          onFillHealthRecord={onFillHealthRecord}
                          onViewEvolution={onViewEvolution}
                          onClearArrived={onClearArrived}
                          onDragStart={(event) => handleDragStart(event, block)}
                          onDragEnd={handleDragEnd}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

AgendaCustomGrid.displayName = "AgendaCustomGrid";

export default AgendaCustomGrid;
